import { Typography } from '@/components';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import styles from './SearchMultiSelect.module.css';

interface SearchMultiSelectProps<T> {
  options: T[];
  initialOptions?: T[];
  placeholder?: string;
  onChange?: (selected: T[]) => void;
  getOptionLabel: (option: T) => string;
  getOptionKey: (option: T) => string | number;
}

const SearchMultiSelect = <T,>({
  options,
  placeholder = 'Choose options',
  onChange,
  getOptionLabel,
  getOptionKey,
}: SearchMultiSelectProps<T>): JSX.Element => {
  const [selectedOptions, setSelectedOptions] = useState<T[]>([]);
  const [filteredOptions, setFilteredOptions] = useState<T[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleOptionToggle = (option: T) => {
    const updatedOptions = selectedOptions.includes(option)
      ? selectedOptions.filter((selectedOption) => selectedOption !== option)
      : [...selectedOptions, option];

    setSelectedOptions(updatedOptions);
    setIsOpen(false);
    resetSearchQuery();
    inputRef.current?.focus();

    if (onChange) {
      onChange(updatedOptions);
    }
  };

  const handleOptionDelete = (option: T) => {
    const updatedOptions = selectedOptions.filter(
      (selectedOption) => selectedOption !== option,
    );

    setSelectedOptions(updatedOptions);
    inputRef.current?.focus();
    if (onChange) {
      onChange(updatedOptions);
    }
  };

  const resetSearchQuery = () => {
    setSearchQuery(null);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const handleOutsideClick = (event: MouseEvent) => {
    const close =
      containerRef.current &&
      !containerRef.current.contains(event.target as Node);
    if (close) {
      setIsOpen(false);
      resetSearchQuery();
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
    } else {
      document.removeEventListener('mousedown', handleOutsideClick);
    }
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [isOpen]);

  useEffect(() => {
    if (searchQuery) {
      setFilteredOptions(
        options.filter((option) =>
          getOptionLabel(option)
            .toLowerCase()
            .includes(searchQuery.toLowerCase()),
        ),
      );
    } else {
      setFilteredOptions(options);
    }
  }, [searchQuery, options]);

  /*
  useEffect(() => {
    if (initialOptions) {
      setSelectedOptions(initialOptions);
    }
  }, [initialOptions]);
  */

  useEffect(() => {
    setSelectedOptions(
      selectedOptions.filter((selectedOption) => {
        return options.includes(selectedOption);
      }),
    );
  }, [options]);

  return (
    <div ref={containerRef} className={styles.container}>
      <div
        className={classNames(styles.inputArea, { [styles.opened]: isOpen })}
        onClick={(event) => {
          setIsOpen((previous) => !previous);
          inputRef.current?.focus();
        }}
      >
        <>
          {selectedOptions.map((option) => (
            <div
              key={getOptionKey(option)}
              onClick={(event) => {
                handleOptionDelete(option);
                event.stopPropagation();
              }}
            >
              <div className={styles.optionChip}>{getOptionLabel(option)}</div>
            </div>
          ))}
          <input
            ref={inputRef}
            placeholder={placeholder}
            className={styles.searchInput}
            onChange={(event) => {
              setIsOpen(true);
              setSearchQuery(event.target.value);
            }}
          ></input>
        </>
      </div>
      {isOpen && (
        <div className={styles.dropdown}>
          {filteredOptions.map((option) => (
            <div
              key={getOptionKey(option)}
              className={classNames(styles.option, {
                [styles.selected]: selectedOptions.includes(option),
              })}
              onClick={() => {
                handleOptionToggle(option);
              }}
            >
              <Typography variant="bodyMedium">
                {getOptionLabel(option)}
              </Typography>
            </div>
          ))}
          {filteredOptions.length === 0 && (
            <div>
              <Typography
                className={styles.dropdownPlaceholder}
                variant="bodyMedium"
              >
                No options
              </Typography>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchMultiSelect;
